import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    fetchProductDetailsAction
} from "../../../../utils/redux/product/productActions";

const ProductDetailsPage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { selectedProduct, isLoading } = useSelector(
        state => state.product
    );

    useEffect(() => {
        dispatch(fetchProductDetailsAction(id));
    }, [id, dispatch]);

    if (isLoading || !selectedProduct) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Loading product details...</p>
                </div>
            </div>
        );
    }

    const {
        name,
        code,
        slug,
        category,
        status,
        isActive,
        collectionType,
        collectionPartner,
        description,
        details,
        specifications,
        calculationInput,
        colorways,
        filters,
        pairedProducts,
        technicalSheetUrl,
        moodShotImage,
        detailCompleted,
        pricingCompleted,
    } = selectedProduct;

    const renderStatusBadge = () => {
        if (status === "COMPLETED") {
            return (
                <span className={`badge ${isActive ? "bg-success" : "bg-warning"}`} style={{ fontSize: "0.9rem", padding: "0.5rem 1rem" }}>
                    <i className={`bi ${isActive ? "bi-check-circle" : "bi-exclamation-triangle"} me-1`}></i>
                    {isActive ? "Active" : "Completed (Inactive)"}
                </span>
            );
        }
        return (
            <span className="badge bg-secondary" style={{ fontSize: "0.9rem", padding: "0.5rem 1rem" }}>
                <i className="bi bi-pencil-square me-1"></i>
                Draft
            </span>
        );
    };

    const renderCompletenessBadge = (isComplete, label) => {
        return (
            <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="text-muted">{label}</span>
                {isComplete ? (
                    <span className="badge bg-success">
                        <i className="bi bi-check-circle me-1"></i>Completed
                    </span>
                ) : (
                    <span className="badge bg-warning text-dark">
                        <i className="bi bi-exclamation-circle me-1"></i>Incomplete
                    </span>
                )}
            </div>
        );
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatObject = (obj = {}) => {
        const formatKey = (key) =>
            key
                .replace(/^_/, "")
                .replace(/([a-z])([A-Z])/g, "$1 $2")
                .replace(/^./, (str) => str.toUpperCase());

        return Object.entries(obj)
            .filter(([key]) => !HIDDEN_KEYS.includes(key))
            .map(([key, value]) => `${formatKey(key)}: ${value}`)
            .join(", ");
    };

    const HIDDEN_KEYS = ["_id", "__v", "createdAt", "updatedAt", "vendorItemPropertyDefinitions"];

    const renderKeyValueList = (obj) => (
        <div className="table-responsive">
            <table className="table table-sm table-borderless mb-0">
                <tbody>
                    {Object.entries(obj)
                        .filter(([key]) => !HIDDEN_KEYS.includes(key))
                        .map(([key, value]) => (
                            <tr key={key}>
                                <td
                                    className="text-muted fw-semibold"
                                    style={{ width: "35%" }}
                                >
                                    {key.replace(/^_/, "")}
                                </td>

                                <td>
                                    {typeof value === "object" && value !== null
                                        ? renderValue(value, key)
                                        : String(value ?? "—")}
                                </td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );

    const renderValue = (value, parentKey = "") => {
        if (value === null || value === undefined) return "—";

        if (
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
        ) {
            return String(value);
        }

        if (Array.isArray(value)) {
            return (
                <div className="d-flex flex-column gap-2">
                    {value.map((item, index) => (
                        <div key={index} className="border rounded p-2">
                            {typeof item === "object"
                                ? formatObject(item)
                                : renderValue(item)}
                        </div>
                    ))}
                </div>
            );
        }



        // Render these as simple key-value tables
        if (parentKey === "properties" || parentKey === "vendorProperties") {
            return renderKeyValueList(value);
        }

        if (
            parentKey === "_baseFabric" ||
            parentKey === "_material"
        ) {
            return renderKeyValueList(
                Object.fromEntries(
                    Object.entries(value).filter(
                        ([key]) => !HIDDEN_KEYS.includes(key)
                    )
                )
            );
        }

        return (
            <div className="ms-3">
                {Object.entries(value)
                    .filter(([key]) => !HIDDEN_KEYS.includes(key))
                    .map(([key, val]) => (
                        <div key={key} className="mb-3">
                            <div className="fw-semibold text-capitalize mb-1">
                                {key.replace(/^_/, "")}
                            </div>
                            <div className="row">
                                {renderValue(val, key)}
                            </div>
                        </div>
                    ))}
            </div>
        );
    };
    const vendorProperties = (props = {}) => {
        const formatKey = (key) =>
            key
                .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase -> camel Case
                .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter

        return Object.entries(props)
            .map(([key, value]) => `${formatKey(key)}: ${value}`)
            .join(", ");
    };

    const renderPricingDetails = () => {
        const adminInputs = calculationInput?.admin_inputs;

        if (!adminInputs) return null;

        return (
            <div className="row g-4">
                {Object.entries(adminInputs).map(([key, value]) => (
                    <div className="col-md-6 col-xl-12" key={key}>
                        <div className="card border h-100">
                            <div className="card-header bg-light">
                                <strong>{key.replace(/^_/, "").replace(/([a-z])([A-Z])/g, "$1 $2")}</strong>
                            </div>

                            <div className="card-body">
                                {typeof value === "object" &&
                                    value !== null &&
                                    !Array.isArray(value) ? (
                                    < div className="row g-3">
                                        {Object.entries(value)
                                            .filter(([k]) => !HIDDEN_KEYS.includes(k))
                                            .map(([k, v]) => (
                                                <div className="col-md-12" key={k}>
                                                    <div className="border rounded p-3 h-100 row">
                                                        <div className="text-muted small mb-2 text-capitalize col-md-6">
                                                            {k.replace(/^_/, "")}
                                                        </div>
                                                        <div className="row">
                                                            {renderValue(v, k)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                ) : (
                                    renderValue(value, key)
                                )}
                            </div>
                        </div>
                    </div>
                ))
                }
            </div >
        );
    };

    return (
        <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
            {/* HEADER CARD */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-start">
                        <div>
                            <div className="d-flex align-items-center gap-3 mb-2">
                                <h2 className="mb-0">{name}</h2>
                                {renderStatusBadge()}
                            </div>
                            <div className="text-muted">
                                <i className="bi bi-upc-scan me-1"></i> Code: <strong>{code}</strong>
                                <span className="mx-2">•</span>
                                <i className="bi bi-link me-1"></i> Slug: {slug}
                            </div>
                        </div>

                        <div className="d-flex gap-2 mt-3 mt-sm-0">
                            {
                                pricingCompleted && <button
                                    className="btn btn-outline-primary"
                                    onClick={() => navigate(`/product/${id}/configurator`)}
                                >
                                    <i className="bi bi-pencil me-1"></i>Check Pricing
                                </button>
                            }

                            <button
                                className="btn btn-outline-primary"
                                onClick={() => navigate(`/products/${id}/edit`)}
                            >
                                <i className="bi bi-pencil me-1"></i>Edit Product
                            </button>

                            {/* {status === "COMPLETED" && (
                                <button
                                    className={`btn ${isActive ? "btn-outline-danger" : "btn-success"}`}
                                >
                                    <i className={`bi ${isActive ? "bi-pause-circle" : "bi-play-circle"} me-1`}></i>
                                    {isActive ? "Deactivate" : "Activate"}
                                </button>
                            )} */}
                        </div>
                    </div>
                </div>
            </div>

            {/* STATUS CARDS */}
            <div className="row g-4 mb-4">
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="text-muted mb-3">
                                <i className="bi bi-info-circle me-2"></i>Category
                            </h6>
                            <h5>
                                {category?.parent?.name && (
                                    <>
                                        <span className="badge bg-light text-dark me-2">{category.parent.name}</span>
                                        <i className="bi bi-arrow-right text-muted me-2"></i>
                                    </>
                                )}
                                <span className="badge bg-primary">{category?.name}</span>
                            </h5>
                            {collectionType && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        <i className="bi bi-collection me-1"></i>
                                        {collectionType} {collectionPartner && `• ${collectionPartner.name}`}
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="text-muted mb-3">
                                <i className="bi bi-check-circle me-2"></i>Completion Status
                            </h6>
                            {renderCompletenessBadge(detailCompleted, "Basic Details")}
                            {renderCompletenessBadge(pricingCompleted, "Pricing")}
                        </div>
                    </div>
                </div>

                <div className="col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <h6 className="text-muted mb-3">
                                <i className="bi bi-images me-2"></i>Media
                            </h6>
                            <div className="d-flex align-items-center gap-3">
                                <div>
                                    <span className="badge bg-light text-dark p-2">
                                        <i className="bi bi-palette me-1"></i>
                                        {colorways?.length || 0} Colorways
                                    </span>
                                </div>
                                <div>
                                    <span className="badge bg-light text-dark p-2">
                                        <i className="bi bi-funnel me-1"></i>
                                        {filters?.length || 0} Filters
                                    </span>
                                </div>
                            </div>
                            {moodShotImage?.url && (
                                <div className="mt-2">
                                    <small className="text-muted">
                                        <i className="bi bi-image me-1"></i>
                                        Mood shot available
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="row g-4">
                {/* LEFT COLUMN - Product Details */}
                <div className="col-lg-8">
                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white border-0 pt-4 pb-0">
                            <h5 className="mb-0">
                                <i className="bi bi-info-circle text-primary me-2"></i>
                                Product Information
                            </h5>
                        </div>
                        <div className="card-body p-4">
                            <div className="mb-4">
                                <h6 className="text-muted mb-2">Description</h6>
                                <p className="mb-0">{description || "—"}</p>
                            </div>

                            <div className="mb-4">
                                <h6 className="text-muted mb-2">Details</h6>
                                <p className="mb-0">{details || "—"}</p>
                            </div>

                            <div className="mb-4">
                                <h6 className="text-muted mb-2">Specifications</h6>
                                <p className="mb-0">{specifications || "—"}</p>
                            </div>

                            {technicalSheetUrl && (
                                <div className="mt-3">
                                    <a
                                        href={technicalSheetUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-outline-primary btn-sm"
                                    >
                                        <i className="bi bi-file-pdf me-1"></i>
                                        View Technical Sheet
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Pricing Details Card */}
                    {calculationInput?.admin_inputs && Object.keys(calculationInput.admin_inputs).length > 0 && (
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white border-0 pt-4 pb-0">
                                <h5 className="mb-0">
                                    <i className="bi bi-calculator text-primary me-2"></i>
                                    Pricing Configuration
                                </h5>
                            </div>
                            <div className="card-body p-4">
                                {renderPricingDetails()}
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT COLUMN - Additional Info */}
                <div className="col-lg-4">
                    {/* Colorways */}
                    {colorways?.length > 0 && (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-header bg-white border-0 pt-4 pb-0">
                                <h6 className="mb-0">
                                    <i className="bi bi-palette text-primary me-2"></i>
                                    Colorways
                                </h6>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {colorways.map((cw, idx) => (
                                        <div key={idx} className="col-6">
                                            <div className="border rounded p-2 text-center">
                                                <div
                                                    className="mx-auto mb-2 rounded-circle"
                                                    style={{
                                                        width: "40px",
                                                        height: "40px",
                                                        backgroundColor: cw.color?.code || "#ccc",
                                                        border: "1px solid #dee2e6"
                                                    }}
                                                ></div>
                                                <small>{cw.color?.name || "Unknown"}</small>
                                                {cw.images?.length > 0 && (
                                                    <div className="mt-1">
                                                        <span className="badge bg-light text-dark">
                                                            {cw.images.length} image{cw.images.length > 1 ? 's' : ''}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Filters */}
                    {filters?.length > 0 && (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-header bg-white border-0 pt-4 pb-0">
                                <h6 className="mb-0">
                                    <i className="bi bi-funnel text-primary me-2"></i>
                                    Filters
                                </h6>
                            </div>
                            <div className="card-body p-4">
                                <div className="d-flex flex-wrap gap-2">
                                    {filters.map(filter => (
                                        <span key={filter._id} className="badge bg-light text-dark p-2">
                                            {filter.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Paired Products */}
                    {pairedProducts?.length > 0 && (
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white border-0 pt-4 pb-0">
                                <h6 className="mb-0">
                                    <i className="bi bi-link text-primary me-2"></i>
                                    Paired Products
                                </h6>
                            </div>
                            <div className="card-body p-4">
                                <div className="list-group list-group-flush">
                                    {pairedProducts.map((pair, idx) => (
                                        <div key={idx} className="list-group-item px-0">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <strong>{pair.product?.name}</strong>
                                                    <br />
                                                    <small className="text-muted">{pair.product?.code}</small>
                                                </div>
                                                {pair.colorway && (
                                                    <span className="badge bg-light text-dark">
                                                        {pair.colorway.name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductDetailsPage;